import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';

import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { connect } from 'react-redux';
import { useAppSelector } from 'dux/app/hooks';

import valuesOf from 'lodash/fp/values';

import { legacyTheme, styled } from '@prose-ui/legacy';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Formik } from 'formik';
import { useGoToCart } from 'hooks/useGoToCart';
import { makeStyles } from 'legacyStyles';

import CheckoutAccountContact from 'Apps/Checkout/Blocks/CheckoutAccountContact';
import CheckoutAddress from 'Apps/Checkout/Blocks/CheckoutAddress';
import CheckoutBrushSummary from 'Apps/Checkout/Blocks/CheckoutBrushSummary';
import CheckoutGiftSummary from 'Apps/Checkout/Blocks/CheckoutGiftSummary';
import { CheckoutLegalBlock } from 'Apps/Checkout/Blocks/CheckoutLegalBlock';
import CheckoutPayment from 'Apps/Checkout/Blocks/CheckoutPayment';
import CheckoutReduceAndReuse from 'Apps/Checkout/Blocks/CheckoutReduceAndReuse';
import CheckoutTicketV2 from 'Apps/Checkout/Blocks/CheckoutTicketV2';
import CheckoutExpressPayment from 'Apps/Checkout/Blocks/ExpressCheckoutPayment';
import ShippingMethod from 'Apps/Checkout/Blocks/ShippingMethod';
import { SubscribeAndSaveSection } from 'Apps/Checkout/Blocks/SubscribeAndSaveSection';
import { SubscribeAndSaveSectionDetailed } from 'Apps/Checkout/Blocks/SubscribeAndSaveSectionDetailed';
import AddressValidationModal from 'Apps/Checkout/Components/AddressValidationModal';
import { RecommendedAddressModal } from 'Apps/Checkout/Components/AddressValidationModalCorrectAddress';
import CheckoutSummaryAccordion from 'Apps/Checkout/Components/CheckoutSummaryAccordion';
import { CHECKOUT_NEW_ADDRESS_FIELD_VALUE } from 'Apps/Checkout/constants/checkoutInputValues';
import * as checkoutSteps from 'Apps/Checkout/constants/checkoutSteps';
import { useCheckoutCategory } from 'Apps/Checkout/hooks/useCheckoutCategory';
import BaseScene from 'Apps/Checkout/Scenes/BaseScene';

import { Button } from 'Components/LegacyButton';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { productsCategories } from 'constants/products';
import * as Status from 'constants/statuses';

import consultationCountriesProps from 'PropTypes/countries';

import addOriginParam from 'utils/addOriginParam';
import useResponsiveVariant from 'utils/useResponsiveVariant';
import { makeValidationsByCountry } from 'utils/validators';

import * as checkoutActions from 'dux/checkout/actions';
import { trackOrderSuccess, trackPurchaseCompleted } from 'dux/tracking/actions';
import * as accountCardsSelectors from 'dux/accountCards/selectors';
import * as checkoutSelectors from 'dux/checkout/selectors';
import * as checkoutAddressesSelectors from 'dux/checkoutAddresses/selectors';
import * as checkoutCartSelectors from 'dux/checkoutCart/selectors';
import { getIsPaymentReady, getPayPalPaymentStatus } from 'dux/checkoutPayment/selectors';
import * as userSelectors from 'dux/user/selectors';
import { useCheckoutOrderQuery } from 'dux/checkout/apiSlice';
import { orderLoadSuccessFromPaymentSuccess } from 'dux/checkoutOrder/thunks';

const CheckoutContent = styled.section`
  grid-column-start: 1;
  width: 100%;
  max-width: 800px;
  padding: ${legacyTheme.props.minimumBlockPadding};
`;

const NextStepButton = styled(Button)`
  height: 48px;
  line-height: 48px;
`;

const useStyles = makeStyles({
  baseScene: {
    display: 'grid',
    alignItems: 'flex-start',
    justifyItems: 'end',
    gridTemplateColumns: '60% 40%',

    [legacyTheme.breakpoints.match('sm')]: {
      display: 'block',
    },
  },
});

const SectionBorder = styled.hr`
  margin: 24px 0;
  border: none;
  border-top: 1px solid rgba(40, 51, 51, 0.2);
`;

const validationForStep = {
  [checkoutSteps.ACCOUNT_DETAILS]: values => ({
    ...makeValidationsByCountry(values, [
      ['account.phone', 'required'],
      ['account.phone', 'phone'],
      ['account.firstName', 'required'],
      ['account.firstName', 'firstName'],
      ['account.lastName', 'required'],
      ['account.lastName', 'lastName'],
      ['account.email', 'required'],
      ['account.email', 'email'],
    ]),
  }),
  [checkoutSteps.SHIPPING_ADDRESS]: values => ({
    ...(values.addressPubkey === CHECKOUT_NEW_ADDRESS_FIELD_VALUE
      ? makeValidationsByCountry(values, [
          ['newAddress.name', 'required'],
          ['newAddress.name', 'fullName'],
          ['newAddress.address1', 'required'],
          ['newAddress.address1', 'address1'],
          ['newAddress.address2', 'address2'],
          ['newAddress.city', 'required'],
          ['newAddress.state', 'required'],
          ['newAddress.state', 'state'],
          ['newAddress.country', 'required'],
          ['newAddress.country', 'country'],
          ['newAddress.zipcode', 'required'],
          ['newAddress.zipcode', 'zipcode'],
        ])
      : {}),
  }),
  [checkoutSteps.PAYMENT]: values => ({
    ...(values.cardPubkey === 'newCard'
      ? makeValidationsByCountry(values, [['nameOnCard', 'required']])
      : {}),
  }),
  // No validation for Success
  [checkoutSteps.SUCCESS]: _values => ({}),
};

const CheckoutForm = ({
  defaultAddressPubkey,
  defaultCardPubkey,
  cartHasSubscription,
  params,
  phone,
  step,
  textMarketingOptin,
  textOptin,
  consultationProfileCountry,
  consultationProfileZipcode,
  dispatch,
  isCheckoutReadyForPayment,
  buttonLabel,
  updateStatusIsLoading,
  user,
  isPaymentReady,
  nextStep,
  hasCompletedAccountDetailsSection,
  hasCompletedShippingAddressSection,
  needAddress,
  isFreeAndCartHasNoSubscription,
}) => {
  const checkoutCategory = useCheckoutCategory();
  const { classes } = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const goToCart = useGoToCart();
  const { isMobile } = useResponsiveVariant();
  const withOrigin = addOriginParam(params);
  const paypalPaymentStatus = useAppSelector(getPayPalPaymentStatus);

  const [paymentError, setPaymentError] = useState(undefined);

  const [searchParams] = useSearchParams();
  const pubkey = searchParams.get('checkout-pubkey') ?? '';

  const [isOrderSuccess, setIsOrderSuccess] = useState(false);

  const { data: checkoutOrderData, isFetching } = useCheckoutOrderQuery(
    { pubkey },
    {
      pollingInterval: 3000,

      // Always skip query if current step is not WAIT_PAYMENT_CONFIRMATION
      // if skipCheckoutOrderPolling is false and step is WAIT_PAYMENT_CONFIRMATION then start polling
      skip: isOrderSuccess || step !== checkoutSteps.WAIT_PAYMENT_CONFIRMATION,
    }
  );

  if (step === checkoutSteps.WAIT_PAYMENT_CONFIRMATION && !isFetching) {
    if (
      checkoutOrderData?.status === 'failure' ||
      checkoutOrderData?.status === 'action_required'
    ) {
      if (!paymentError) {
        setPaymentError(checkoutOrderData?.description);
      }
      navigate(`/checkout/${checkoutCategory}/${checkoutSteps.PAYMENT}`);
    }

    if (checkoutOrderData?.status === 'success') {
      dispatch(orderLoadSuccessFromPaymentSuccess(checkoutOrderData.order));
      dispatch(trackOrderSuccess(checkoutOrderData.order));
      dispatch(
        trackPurchaseCompleted({
          express_checkout: false,
          payment_location: 'cart',
          payment_type: 'Credit Card',
        })
      );
      setIsOrderSuccess(true);
      navigate(`/checkout/success?order-pubkey=${checkoutOrderData.order.pubkey}`);
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', goToCart);

    return () => {
      window.removeEventListener('popstate', goToCart);
    };
  }, []);

  const onSubmit = async (values, actions) => {
    // // @todo: find a way to incorporate this to if statement
    setPaymentError(undefined); // Clear previous errors
    // setskipCheckoutOrderPolling(false);
    const cardElement = elements.getElement(CardElement);

    const stepByStepCheckoutSubmit = checkoutActions.createStepByStepCheckoutSubmit({
      checkoutCategory,
      step,
      stripe,
      params,
      cardElement,
    });
    const { nextRoute } = await dispatch(stepByStepCheckoutSubmit(values, actions));
    navigate(nextRoute);
  };

  const shippingAddressInitialValues = {
    addressPubkey: defaultAddressPubkey || CHECKOUT_NEW_ADDRESS_FIELD_VALUE,
    newAddress: {
      name: user ? `${user.first_name} ${user.last_name}` : '',
      address1: '',
      address2: '',
      city: '',
      country: consultationProfileCountry || '',
      state: '',
      zipcode: consultationProfileZipcode || '',
      label: '',
    },
  };

  const accountContactInitialValues = {
    phone,
    textOptin: Boolean(textOptin),
    textMarketingOptin: Boolean(textMarketingOptin),
    account: {
      firstName: user?.first_name ?? '',
      lastName: user?.last_name ?? '',
      email: user?.email ?? '',
      phone,
    },
  };

  const cardInitialValues = {
    cardPubkey: defaultCardPubkey || 'newCard',
    nameOnCard: '',
    saveCard: Boolean(cartHasSubscription),
  };

  const isCtaButtonLoading = (isSubmitting, cardPubkey) => {
    switch (step) {
      case checkoutSteps.PAYMENT:
        return (
          (!isPaymentReady && cardPubkey === 'newCard') ||
          isSubmitting ||
          updateStatusIsLoading ||
          !isCheckoutReadyForPayment ||
          paypalPaymentStatus === Status.LOADING
        );
      case checkoutSteps.WAIT_PAYMENT_CONFIRMATION:
        // return !skipCheckoutOrderPolling; // return true if polling is not finished
        return true;
      default:
        return isSubmitting;
    }
  };

  const isButtonDisabled =
    (step === checkoutSteps.ACCOUNT_DETAILS && hasCompletedAccountDetailsSection) ||
    (step === checkoutSteps.SHIPPING_ADDRESS &&
      hasCompletedShippingAddressSection &&
      !isFreeAndCartHasNoSubscription);

  if (needAddress && step === checkoutSteps.PAYMENT && !hasCompletedAccountDetailsSection) {
    return (
      <Navigate
        replace
        to={withOrigin(`/checkout/${checkoutCategory}/${checkoutSteps.ACCOUNT_DETAILS}`)}
      />
    );
  }

  return (
    <Formik
      initialValues={{
        // CheckoutReduceAndReuse
        // Pumps opt-out by default
        wantsPumps: false,
        // CheckoutAccountContact
        ...accountContactInitialValues,
        // ConnectedCheckoutAddress
        ...shippingAddressInitialValues,
        // ConnectedCheckoutPayment
        ...cardInitialValues,
      }}
      onSubmit={onSubmit}
      validate={validationForStep[step]}
    >
      {({ values, handleSubmit, isSubmitting, resetForm, setFieldValue }) => (
        <form autoComplete="off" onSubmit={handleSubmit}>
          <BaseScene classes={{ content: classes.baseScene }} justifyContent="start">
            {!isMobile && <Spacer size={32} />}
            {isMobile && (
              <>
                <CheckoutSummaryAccordion
                  isSubmitting={isSubmitting}
                  step={step}
                  wantsPumps={values.wantsPumps}
                />
                <CheckoutReduceAndReuse />
              </>
            )}

            <CheckoutContent>
              {!isMobile && (
                <>
                  <Typography align="center" markupName="h1" variant="h2">
                    Checkout
                  </Typography>
                  <SectionBorder />

                  <CheckoutReduceAndReuse />
                </>
              )}

              <CheckoutBrushSummary />

              <CheckoutGiftSummary />

              {needAddress && (
                <>
                  <AddressValidationModal
                    address={values.newAddress}
                    isSubmitting={isSubmitting}
                    step={step}
                    submit={handleSubmit}
                  />
                  <RecommendedAddressModal
                    address={values.newAddress}
                    isSubmitting={isSubmitting}
                    setFieldValue={setFieldValue}
                    step={step}
                    submit={handleSubmit}
                  />
                  <CheckoutAccountContact
                    isCurrentStep={step === checkoutSteps.ACCOUNT_DETAILS}
                    isSubmitting={isSubmitting}
                    onCancel={() => {
                      resetForm({
                        values: {
                          ...values,
                          ...accountContactInitialValues,
                        },
                      });
                      navigate(withOrigin(`/checkout/${checkoutCategory}/${nextStep}`));
                    }}
                    params={params}
                    phone={values.phone}
                    textMarketingOptin={values.textMarketingOptin}
                    textOptin={values.textOptin}
                  />
                  {checkoutCategory === productsCategories.HAIRCARE &&
                    hasCompletedShippingAddressSection &&
                    cartHasSubscription && <SubscribeAndSaveSection />}

                  {checkoutCategory === productsCategories.SKINCARE &&
                    hasCompletedShippingAddressSection &&
                    cartHasSubscription && <SubscribeAndSaveSectionDetailed />}

                  {/* Express checkout flow */}
                  {step !== checkoutSteps.PAYMENT && (
                    <CheckoutExpressPayment isLastSection={!hasCompletedAccountDetailsSection} />
                  )}

                  {hasCompletedAccountDetailsSection && (
                    <CheckoutAddress
                      isCurrentStep={step === checkoutSteps.SHIPPING_ADDRESS}
                      isSubmitting={isSubmitting}
                      newAddress={values.newAddress}
                      onCancel={() => {
                        resetForm({
                          values: {
                            ...values,
                            ...shippingAddressInitialValues,
                          },
                        });
                        navigate(withOrigin(`/checkout/${checkoutCategory}/${nextStep}`));
                      }}
                      params={params}
                      resetNewAddressForm={() =>
                        setFieldValue('newAddress', shippingAddressInitialValues.newAddress)
                      }
                    />
                  )}

                  {hasCompletedShippingAddressSection && !cartHasSubscription && <ShippingMethod />}
                </>
              )}

              {/* Traditional payment flow */}
              {(hasCompletedShippingAddressSection || !needAddress) &&
                step !== checkoutSteps.WAIT_PAYMENT_CONFIRMATION && (
                  <CheckoutPayment
                    cardPubkey={values.cardPubkey}
                    params={params}
                    paymentError={paymentError}
                    saveCard={values.saveCard}
                    step={step}
                  />
                )}

              <NextStepButton
                data-testid="checkout-next-step-button"
                disabled={isButtonDisabled || isCtaButtonLoading(isSubmitting, values.cardPubkey)}
                fullWidth
                isLoading={isCtaButtonLoading(isSubmitting, values.cardPubkey)}
                noMargin
                type="submit"
                variant="vert"
              >
                {buttonLabel}
              </NextStepButton>

              {step === checkoutSteps.PAYMENT && <CheckoutLegalBlock />}
            </CheckoutContent>

            {!isMobile && (
              <CheckoutTicketV2
                ctaButtonProps={{
                  disabled: isButtonDisabled || isCtaButtonLoading(isSubmitting, values.cardPubkey),
                  isLoading: isCtaButtonLoading(isSubmitting, values.cardPubkey),
                  text: buttonLabel,
                  type: 'submit',
                  variant: 'vert',
                }}
                isCheckout
                wantsPumps={values.wantsPumps}
              />
            )}
          </BaseScene>
        </form>
      )}
    </Formik>
  );
};

CheckoutForm.propTypes = forbidExtraProps({
  defaultAddressPubkey: PropTypes.string,
  defaultCardPubkey: PropTypes.string,
  cartHasSubscription: PropTypes.bool,
  params: PropTypes.shape({
    has: PropTypes.func.isRequired,
    get: PropTypes.func.isRequired,
  }).isRequired,
  phone: PropTypes.string.isRequired,
  step: PropTypes.oneOf(valuesOf(checkoutSteps)).isRequired,
  textMarketingOptin: PropTypes.bool,
  textOptin: PropTypes.bool,
  consultationProfileCountry: consultationCountriesProps,
  consultationProfileZipcode: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  isCheckoutReadyForPayment: PropTypes.bool.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  updateStatusIsLoading: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  isPaymentReady: PropTypes.bool.isRequired,
  nextStep: PropTypes.oneOf(valuesOf(checkoutSteps)),
  shippingAddress: PropTypes.shape({
    address1: PropTypes.string.isRequired,
    address2: PropTypes.string,
    city: PropTypes.string.isRequired,
    is_subscription_address: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    primary: PropTypes.bool,
    pubkey: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zipcode: PropTypes.string.isRequired,
    country: PropTypes.string,
  }),
  hasCompletedAccountDetailsSection: PropTypes.bool.isRequired,
  hasCompletedShippingAddressSection: PropTypes.bool.isRequired,
  needAddress: PropTypes.bool.isRequired,
  isFreeAndCartHasNoSubscription: PropTypes.bool.isRequired,
});

CheckoutForm.defaultProps = {
  defaultAddressPubkey: null,
  defaultCardPubkey: null,
  cartHasSubscription: false,
  textMarketingOptin: false,
  textOptin: false,
  consultationProfileCountry: null,
  consultationProfileZipcode: null,
  shippingAddress: null,
  nextStep: null,
};

export default connect(
  (state, { step }) => ({
    defaultAddressPubkey: checkoutAddressesSelectors.getDefaultAddressPubkey(state),
    defaultCardPubkey: accountCardsSelectors.getPreselectedCardId(state),
    cartHasSubscription: checkoutCartSelectors.cartHasSubscription(state),
    phone: userSelectors.getUserPhone(state),
    textOptin: userSelectors.getTextOptin(state), // This might return null
    textMarketingOptin: userSelectors.getTextMarketingOptin(state), // This might return null
    consultationProfileCountry:
      checkoutCartSelectors.getConsultationProfileCountryInCartSurvey(state),
    consultationProfileZipcode:
      checkoutCartSelectors.getConsultationProfileZipcodenCartSurvey(state),
    isCheckoutReadyForPayment: checkoutSelectors.getIsCheckoutReadyForPayment(state),
    updateStatusIsLoading: checkoutCartSelectors.getUpdateStatusIsLoading(state),
    buttonLabel: checkoutSelectors.getCTAButtonLabelFromStepProp(state, { step }),
    user: userSelectors.getUser(state),
    isPaymentReady: getIsPaymentReady(state),
    nextStep: checkoutSelectors.getNextStepFromStepProp(state, { step }),
    hasCompletedAccountDetailsSection:
      checkoutSelectors.getHasCompletedAccountDetailsSection(state),
    hasCompletedShippingAddressSection:
      checkoutSelectors.getHasCompletedShippingAddressSection(state),
    needAddress: checkoutCartSelectors.getCartNeedAddress(state),
    isFreeAndCartHasNoSubscription: checkoutCartSelectors.getIsFreeAndCartHasNoSubscription(state),
  }),
  dispatch => ({ dispatch })
)(CheckoutForm);
